import {
  Avatar,
  Button,
  Divider,
  Form,
  Input,
  Row,
  Switch,
  Tooltip,
} from "antd";
import { FC, useMemo } from "react";
import classNames from "classnames";

import styles from "./index.less";

import { FlowNodeInputItemType } from "@packages/flow-core/module/node/types";
import { ReactComponent as RefreshIcon } from "../../assets/refresh.svg";
import HumanIcon from "../../assets/imgs/human.png";
import { StreamResponseDataType } from "@/FlowEditPage/utils/streamFetch";

interface ChatItemPluginInputProps {
  inputs: FlowNodeInputItemType[];
  loading: boolean;
  onRunPlugin: (values: Record<string, unknown>) => void;
  disableToSubmit: boolean; // 限制提交

  onRefresh: (values: Record<string, unknown>) => void;
  streamResponseData?: StreamResponseDataType[];
  onViewDetail: () => void;
}

/**
 * 直接渲染输出表单
 * @returns
 */
const ChatItemPluginInput: FC<ChatItemPluginInputProps> = ({
  inputs,
  loading,
  streamResponseData,
  disableToSubmit,
  onRunPlugin,
  onRefresh,
  onViewDetail,
}) => {
  const [form] = Form.useForm();

  const totalTime = useMemo(() => {
    if (streamResponseData) {
      const maxEndTime = Math.max(
        ...streamResponseData.map((d) => d.endTime).filter((d) => !!d)
      );
      const minBeginTime = Math.min(
        ...streamResponseData.map((d) => d.beginTime).filter((d) => !!d)
      );
      return (maxEndTime - minBeginTime) / 1000;
    }
    return 0;
  }, [streamResponseData]);

  const handleSubmit = async (type: "submit" | "refresh") => {
    try {
      const data = await form.validateFields();
      if (type === "submit") {
        onRunPlugin(data);
      } else {
        onRefresh(data);
      }
    } catch (err) {}
  };

  return (
    <div className={classNames(styles["chat-item-human"], styles["chat-item"])}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
        }}
        className={styles["message-box"]}
      >
        <div className={styles["chat-item-human-content"]}>
          <div className={styles["plugin-input-container"]}>
            <div className={styles["plugin-input-title"]}>
              请输入流程所需参数
            </div>
            <Divider style={{ margin: "8px 0 20px 0" }} />
            <Form
              form={form}
              labelCol={{
                span: 10,
              }}
              labelWrap={true}
              layout="vertical"
            >
              {inputs.map((input) => (
                <Form.Item
                  key={input.key}
                  name={input.key}
                  label={input.label}
                  initialValue={input.value}
                  required={input.required}
                  rules={[
                    {
                      required: input.required,
                    },
                  ]}
                >
                  {input.valueType === "string" && <Input.TextArea />}
                  {input.valueType === "number" && <Input type="number" />}
                  {input.valueType === "bool" && <Switch />}
                  {!["string", "number", "bool"].includes(
                    input.valueType ?? "string"
                  ) && <Input.TextArea />}
                </Form.Item>
              ))}
            </Form>

            <Button
              type="primary"
              onClick={() => handleSubmit("submit")}
              disabled={disableToSubmit}
              className={styles["plugin-input-submit-btn"]}
              title="点击提交子流程输入参数"
            >
              {disableToSubmit ? "已提交，等待 AI 回复" : "提交"}
            </Button>
          </div>
          {!loading && streamResponseData && (
            <>
              <Divider style={{ margin: "7px 0" }} />
              <Row justify="space-between">
                <span className={styles.time}>{`${totalTime}s`}</span>
                <a className={styles.tag} color="red" onClick={onViewDetail}>
                  查看详情
                </a>
              </Row>
            </>
          )}
          {loading && (
            <>
              <Divider style={{ margin: "7px 0" }} />
              <div className={styles.loading}>
                <div />
                <div />
                <div />
                <div />
                <div />
              </div>
            </>
          )}
        </div>
        <div className={styles["action-area"]}>
          <div className={styles.action}>
            <Tooltip title="重新生成" placement="bottom">
              <Button
                type="text"
                icon={<RefreshIcon />}
                onClick={() => handleSubmit("refresh")}
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <Avatar shape="square" src={HumanIcon} className={styles.avatar} />
    </div>
  );
};

export default ChatItemPluginInput;
